import { X, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import { SuggestedActions } from './SuggestedActions'
import { useChatStore } from '@/stores'
import { useUiStore } from '@/stores'
import { useStreamChat } from '@/hooks'
import { useClearChat } from '@/hooks'
import { usePageContext } from '@/hooks/usePageContext'
import { cn } from '@/lib/utils'

interface ChatSlideoutProps {
  className?: string
}

export function ChatSlideout({ className }: ChatSlideoutProps) {
  const { chatOpen, toggleChat } = useUiStore()
  const { messages, isLoading } = useChatStore()
  const { send, cancel } = useStreamChat()
  const clearChat = useClearChat()
  const pageContext = usePageContext()

  // Determine if we are actively streaming (loading + last message is streaming)
  const lastMessage = messages[messages.length - 1]
  const isStreaming = isLoading && lastMessage?.isStreaming === true

  const handleSend = (message: string) => {
    send(message, pageContext)
  }

  const handleSuggestion = (message: string) => {
    send(message, pageContext)
  }

  const handleClear = () => {
    clearChat()
  }

  return (
    <>
      {/* Backdrop (mobile) */}
      {chatOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleChat}
        />
      )}

      {/* Slideout panel */}
      <div
        role="dialog"
        aria-label="Chat with CA Copilot"
        className={cn(
          'fixed right-0 top-14 bottom-0 z-50 w-full sm:w-[440px] lg:w-[480px] chat-panel-bg border-l border-primary/15',
          'flex flex-col',
          'transform transition-transform duration-300 ease-in-out',
          chatOpen ? 'translate-x-0 shadow-[-6px_0_24px_-4px_hsl(var(--primary)/0.08)]' : 'translate-x-full',
          className
        )}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between p-4 border-b border-border/30")}>
          <h2 className="font-semibold bg-gradient-to-r from-primary to-[hsl(280,100%,70%)] bg-clip-text text-transparent">CA Copilot</h2>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClear}
                title="Clear chat"
                aria-label="Clear chat"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleChat}
              title="Close chat"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className={cn("bg-gradient-to-r from-transparent via-primary/20 to-transparent h-px", isStreaming && "animate-shimmer")} />

        {/* Messages */}
        <MessageList messages={messages} isLoading={isLoading && !isStreaming} />

        {/* Suggested actions (only when no messages) */}
        {messages.length === 0 && (
          <SuggestedActions onSelect={handleSuggestion} disabled={isLoading} />
        )}

        {/* Input */}
        <ChatInput
          onSend={handleSend}
          onCancel={cancel}
          isLoading={isLoading}
          isStreaming={isStreaming}
          placeholder="Ask about tasks, deadlines, compliance..."
        />
      </div>
    </>
  )
}
